import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { useIsAdmin } from '../api/auth';
import { CATALOGUE_SECTIONS } from '../App';
import { fmtTime } from '../lib/format';
import { ROLE_COLOUR, ROLE_ICON, ROLE_LABEL, type DeviceProfile } from './LineView';

/**
 * The equipment catalogue: everything that can go on a line, and what it costs.
 *
 * Its own section rather than a panel at the bottom of the line page, because
 * the two jobs are different. Configuring a line is "which of these do I put
 * where"; this is "what do we sell, at what price, under which article code" —
 * and that second one gets edited by someone who is not standing at a machine.
 *
 * Prices here become products in the invoicing system, keyed on SKU. A change
 * made on this page reaches a quote only after a sync, which the page says
 * plainly rather than leaving someone to wonder why yesterday's price is on
 * today's document.
 */

const ROLE_ORDER = [
  'camera',
  'trigger',
  'rejector',
  'collector',
  'feeder',
  'conveyor',
  'encoder',
  'light',
  'io',
  'plc',
  'safety',
  'mechanical',
];

export function CatalogueView() {
  const isAdmin = useIsAdmin();
  const [profiles, setProfiles] = useState<DeviceProfile[]>([]);
  const { category } = useParams();
  /**
   * The URL decides the section; the dropdown only narrows within it.
   *
   * Two filters over the same field would fight — picking "rejector" from the
   * dropdown while standing on /catalogue/cameras would show nothing and look
   * broken. Inside a section the dropdown is hidden entirely.
   */
  const section = CATALOGUE_SECTIONS.find((c) => c.slug === category) ?? null;
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState<Record<number, Record<string, string>>>({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setProfiles(await api.listDeviceProfiles());
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (!isAdmin) {
    return (
      <div>
        <h1 className="view-title">Catalogue</h1>
        <div className="panel muted">Managing the catalogue requires the ADMIN role.</div>
      </div>
    );
  }

  // 'Controls & other' is everything no section claims, so it cannot go stale
  // when a new role is added — a role without a section still has a home.
  const claimed = CATALOGUE_SECTIONS.map((c) => c.role).filter(Boolean) as string[];
  const shown = profiles
    .filter((p) =>
      section
        ? section.role
          ? p.role === section.role
          : !claimed.includes(p.role)
        : !roleFilter || p.role === roleFilter,
    )
    .filter((p) =>
      !search ||
      `${p.brand} ${p.model} ${p.sku ?? ''} ${p.vendor ?? ''}`.toLowerCase().includes(search.toLowerCase()),
    )
    .sort(
      (a, b) =>
        ROLE_ORDER.indexOf(a.role) - ROLE_ORDER.indexOf(b.role) ||
        a.brand.localeCompare(b.brand) ||
        a.model.localeCompare(b.model),
    );

  const priced = profiles.filter((p) => p.listPrice !== null).length;
  const inCatalogue = profiles.filter((p) => p.invoiceProductId).length;
  const roles = [...new Set(profiles.map((p) => p.role))].sort(
    (a, b) => ROLE_ORDER.indexOf(a) - ROLE_ORDER.indexOf(b),
  );

  const edit = (id: number, field: string, value: string) =>
    setDirty((d) => ({ ...d, [id]: { ...(d[id] ?? {}), [field]: value } }));

  const valueOf = (p: DeviceProfile, field: string, current: string | number | null) =>
    dirty[p.id]?.[field] ?? (current === null || current === undefined ? '' : String(current));

  const commit = async (p: DeviceProfile, field: string, current: string | number | null) => {
    const raw = dirty[p.id]?.[field];
    if (raw === undefined) return;
    const numeric = ['listPrice', 'installHours', 'actuationMs', 'dwellMs'].includes(field);
    const next = raw === '' ? null : numeric ? Number(raw) : raw;
    if (next === current || (next === null && current === null)) {
      setDirty((d) => {
        const { [field]: _drop, ...rest } = d[p.id] ?? {};
        return { ...d, [p.id]: rest };
      });
      return;
    }
    try {
      await api.updateDeviceProfile(p.id, { [field]: next });
      setDirty((d) => {
        const { [field]: _drop, ...rest } = d[p.id] ?? {};
        return { ...d, [p.id]: rest };
      });
      await load();
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const remove = async (p: DeviceProfile) => {
    if (
      !window.confirm(
        `Remove ${p.brand} ${p.model} from the catalogue?\n\n` +
          (p.invoiceProductId
            ? 'It stays on any invoice already issued — it is deactivated in the invoicing system, not deleted, so those documents keep working.'
            : 'It has never been sent to the invoicing system.'),
      )
    ) {
      return;
    }
    try {
      await api.deleteDeviceProfile(p.id);
      setNotice(`${p.brand} ${p.model} removed.`);
      setError(null);
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const cell: React.CSSProperties = { padding: '6px 8px', fontSize: 12 };
  const input: React.CSSProperties = { width: '100%', padding: '3px 6px', fontSize: 12 };

  return (
    <div>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h1 className="view-title">
          {section ? `${section.icon} ${section.label}` : 'Catalogue'}
        </h1>
        <button
          className="btn primary"
          disabled={busy}
          title="Push priced equipment to the invoicing system as products"
          onClick={async () => {
            setBusy(true);
            try {
              const r = await api.syncCatalogue();
              setNotice(
                `${r.pushed} items pushed (${r.created} new, ${r.updated} updated).` +
                  (r.skipped.length ? ` Not pushed: ${r.skipped.length}.` : ''),
              );
              setError(null);
              await load();
            } catch (e) {
              setError((e as Error).message);
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? 'Syncing…' : '⇪ Sync to invoicing'}
        </button>
      </div>

      <div className="panel" style={{ marginBottom: 16 }}>
        <div className="row" style={{ gap: 24, flexWrap: 'wrap' }}>
          <span>
            <strong style={{ fontSize: 20 }}>{shown.length}</strong>{' '}
            <span className="muted">{section ? section.label.toLowerCase() : 'items'}</span>
          </span>
          <span>
            <strong style={{ fontSize: 20 }}>
              {section ? shown.filter((p) => p.listPrice !== null).length : priced}
            </strong>{' '}
            <span className="muted">with a price</span>
          </span>
          <span>
            <strong style={{ fontSize: 20 }}>
              {section ? shown.filter((p) => p.invoiceProductId !== null).length : inCatalogue}
            </strong>{' '}
            <span className="muted">in the invoicing catalogue</span>
          </span>
        </div>
        {/* The gap between editing here and it reaching a quote is real, so it
            is stated rather than left to be discovered on a document. */}
        <p className="muted" style={{ fontSize: 12, margin: '10px 0 0' }}>
          Prices become products in the invoicing system, matched on SKU. A change here reaches a
          quote only after a sync — and a quote already sent keeps the price it was frozen at.
        </p>
      </div>

      {section && (
        <div className="panel" style={{ marginBottom: 16 }}>
          {section.browse ? (
            <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
              <span className="muted" style={{ fontSize: 13 }}>
                Everything here is yours — chosen, priced, quotable. To add one, pick it from a
                supplier's range first.
              </span>
              <Link className="btn" to={`/catalogue/${section.slug}/find`}>
                Browse supplier ranges →
              </Link>
            </div>
          ) : (
            /* Said out loud rather than shown as an empty picker: no supplier
               range exists for this yet. An absent feature and a broken one look
               identical if nobody names the difference. */
            <span className="muted" style={{ fontSize: 13 }}>
              No supplier range is loaded for {section.label.toLowerCase()} yet, so these are items
              you have entered. Add one with the form below.
            </span>
          )}
        </div>
      )}

      {error && <div className="panel" style={{ color: 'var(--bad)', marginBottom: 16 }}>{error}</div>}
      {notice && <div className="panel" style={{ color: 'var(--good)', marginBottom: 16 }}>{notice}</div>}

      <div className="panel">
        <div className="row" style={{ gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
          <input
            placeholder="Search brand, model, SKU…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: 200 }}
          />
          {!section && (
          <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
            <option value="">All kinds</option>
            {roles.map((r) => (
              <option key={r} value={r}>
                {ROLE_LABEL[r] ?? r}
              </option>
            ))}
          </select>
          )}
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th style={cell}>Item</th>
                <th style={cell}>SKU</th>
                <th style={cell}>Supplier</th>
                <th style={{ ...cell, textAlign: 'right' }}>Price</th>
                <th style={{ ...cell, textAlign: 'right' }}>Install</th>
                <th style={{ ...cell, textAlign: 'right' }}>Acts / holds</th>
                <th style={cell}>State</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {shown.map((p) => (
                <tr key={p.id}>
                  <td style={cell}>
                    <span style={{ color: ROLE_COLOUR[p.role] ?? 'var(--text-dim)' }}>
                      {ROLE_ICON[p.role] ?? '▪'}
                    </span>{' '}
                    <Link
                      to={`/catalogue/item/${p.id}`}
                      style={{ color: 'inherit', textDecoration: 'none' }}
                      title="Open the full item — supplier price, margin, specification, where it is used"
                    >
                      <strong>{p.brand}</strong> {p.model}
                    </Link>
                    <div className="muted" style={{ fontSize: 11 }}>
                      {ROLE_LABEL[p.role] ?? p.role} · {p.driver}
                    </div>
                  </td>
                  <td style={{ ...cell, width: 130 }}>
                    <input
                      value={valueOf(p, 'sku', p.sku)}
                      placeholder="none"
                      onChange={(e) => edit(p.id, 'sku', e.target.value)}
                      onBlur={() => void commit(p, 'sku', p.sku)}
                      style={{
                        ...input,
                        borderColor: p.sku ? undefined : 'var(--warn)',
                        fontFamily: 'var(--mono)',
                      }}
                    />
                  </td>
                  <td style={{ ...cell, width: 130 }}>
                    <input
                      value={valueOf(p, 'vendor', p.vendor)}
                      placeholder="—"
                      onChange={(e) => edit(p.id, 'vendor', e.target.value)}
                      onBlur={() => void commit(p, 'vendor', p.vendor)}
                      style={input}
                    />
                  </td>
                  <td style={{ ...cell, width: 100 }}>
                    <input
                      value={valueOf(p, 'listPrice', p.listPrice)}
                      placeholder="none"
                      onChange={(e) => edit(p.id, 'listPrice', e.target.value)}
                      onBlur={() => void commit(p, 'listPrice', p.listPrice)}
                      style={{
                        ...input,
                        textAlign: 'right',
                        borderColor: p.listPrice === null ? 'var(--warn)' : undefined,
                      }}
                    />
                  </td>
                  <td style={{ ...cell, width: 80 }}>
                    <input
                      value={valueOf(p, 'installHours', p.installHours)}
                      placeholder="—"
                      onChange={(e) => edit(p.id, 'installHours', e.target.value)}
                      onBlur={() => void commit(p, 'installHours', p.installHours)}
                      style={{ ...input, textAlign: 'right' }}
                    />
                  </td>
                  <td style={{ ...cell, textAlign: 'right', whiteSpace: 'nowrap' }} className="muted">
                    {p.actuationMs ?? '—'} / {p.dwellMs ?? '—'} ms
                  </td>
                  <td style={cell}>
                    {/* Two different facts, kept apart. Verified is about the
                        address being real; in-catalogue is about the price
                        having reached invoicing. */}
                    {p.verified ? (
                      <span style={{ color: 'var(--good)', fontSize: 11 }}>● verified</span>
                    ) : (
                      <span style={{ color: 'var(--warn)', fontSize: 11 }}>▲ unverified</span>
                    )}
                    {p.invoiceProductId ? (
                      <div className="muted" style={{ fontSize: 10 }}>
                        catalogue #{p.invoiceProductId}
                        {p.catalogueSyncedAt && ` · ${fmtTime(p.catalogueSyncedAt).slice(0, 10)}`}
                      </div>
                    ) : (
                      <div className="muted" style={{ fontSize: 10 }}>not synced</div>
                    )}
                  </td>
                  <td style={{ ...cell, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button
                      className="btn sm"
                      title={
                        p.verified
                          ? 'Mark as unverified again'
                          : 'Confirm you have seen this drive real hardware'
                      }
                      onClick={async () => {
                        if (
                          !p.verified &&
                          !window.confirm(
                            'Only mark this verified if you have watched it drive the real device.\n\n' +
                              'A wrong address does not raise an error — it moves something.',
                          )
                        ) {
                          return;
                        }
                        await api.verifyDeviceProfile(p.id, !p.verified);
                        await load();
                      }}
                    >
                      {p.verified ? '⊘' : '✓'}
                    </button>
                    <button className="btn sm" onClick={() => void remove(p)} title="Remove">
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
              {shown.length === 0 && (
                <tr>
                  <td colSpan={8} className="muted" style={cell}>
                    {loading ? 'Loading…' : 'Nothing matches.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <p className="muted" style={{ fontSize: 12, marginTop: 12, marginBottom: 0 }}>
          Fields save when you click away. An item with no SKU or no price is skipped by the sync
          rather than pushed under an invented code — that is why those boxes are outlined.
        </p>
      </div>
    </div>
  );
}
